import * as Yup from 'yup';
import { Field, FieldProps, Formik, FormikHelpers, FormikProps } from 'formik';
import { FormHelperText, Stack } from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';
import { PaloNextThemeProvider } from '@synergy/theme-palo';
import {
    PoolButton,
    PoolFormikSelect,
    PoolModal,
    PoolModalActions,
    PoolModalContent,
    PoolModalProps,
    PoolModalTitle,
} from '@synergy/components-next';
import { PoolMarkdownEditor, ScrollToFieldError, SelectOption } from '@synergy/components';
import { ReactElement, ReactNode } from 'react';
import { ReceiptAlertType, getReceiptAlertTypeTranslationId } from '@synergy/models';
import { fields } from '@synergy/utils';

import { InternalNote } from '../../models/internal-note';
import { InternalNoteType } from '../../models/enums/internal-note-type';
import { NoteVisibility, getNoteVisibilityTranslationId } from '../../models/enums/note-visibility';

export interface InternalNoteFormValue {
    id?: number;
    type: InternalNoteType;
    text: string;
    visibility: NoteVisibility[];
}

export interface InternalNotesModalProps extends PoolModalProps {
    internalNote?: InternalNote;
    onSave: (internalNote: InternalNoteFormValue) => void;
    onClose: () => void;
}

const fieldNames = fields<InternalNoteFormValue>();

export const InternalNotesModal = (props: InternalNotesModalProps): ReactElement => {
    const { internalNote, onClose, onSave, open } = props;
    const intl = useIntl();

    const initialValues: InternalNoteFormValue = {
        id: internalNote?.id,
        type: internalNote?.type ?? InternalNoteType.Info,
        text: internalNote?.text ?? '',
        visibility: internalNote?.visibility ?? [],
    };

    const internalNotesTypeOptions: SelectOption[] = Object.values(ReceiptAlertType).map(receiptAlertType => {
        return {
            value: receiptAlertType,
            displayValue: <FormattedMessage id={getReceiptAlertTypeTranslationId(receiptAlertType)} />,
        };
    });

    const internalNoteVisibilityOptions: SelectOption[] = Object.values(NoteVisibility).map(visibility => {
        return {
            value: visibility,
            displayValue: <FormattedMessage id={getNoteVisibilityTranslationId(visibility)} />,
        };
    });

    const renderVisibilityValue = (value: '' | NoteVisibility[]): ReactNode => {
        if (value === '') {
            return <FormattedMessage id="app.common.all" />;
        }

        return value
            .map(visibility => intl.formatMessage({ id: getNoteVisibilityTranslationId(visibility) }))
            .join(', ');
    };

    const handleClose = (formikProps: FormikProps<InternalNoteFormValue> | FormikHelpers<InternalNoteFormValue>) => {
        setTimeout(() => formikProps.resetForm(), 300);
        onClose();
    };

    const handleSubmit = (values: InternalNoteFormValue, formikHelpers: FormikHelpers<InternalNoteFormValue>) => {
        onSave(values);
        handleClose(formikHelpers);
    };

    const validationSchema = Yup.object().shape({
        type: Yup.string().required('app.error.required'),
        text: Yup.string().required('app.error.required'),
        visibility: Yup.array().min(1, 'app.error.required').of(Yup.string().required('app.error.required')),
    });

    return (
        <PaloNextThemeProvider>
            <Formik
                initialValues={initialValues}
                onSubmit={handleSubmit}
                validationSchema={validationSchema}
                enableReinitialize={true}
            >
                {formikProps => (
                    <PoolModal open={open} onClose={() => handleClose(formikProps)}>
                        <PoolModalTitle>
                            <FormattedMessage id={internalNote ? 'app.internalNotes.edit' : 'app.internalNotes.add'} />
                        </PoolModalTitle>
                        <PoolModalContent>
                            <ScrollToFieldError />
                            <Stack spacing={2}>
                                <PoolFormikSelect
                                    name={fieldNames.type}
                                    label={<FormattedMessage id="app.label.type" />}
                                    fullWidth={true}
                                    options={internalNotesTypeOptions}
                                    required={true}
                                />
                                <Field name={fieldNames.text}>
                                    {({ field, form, meta }: FieldProps<string>) => (
                                        <>
                                            <PoolMarkdownEditor
                                                {...field}
                                                value={field.value}
                                                onChange={value => form.setFieldValue('text', value)}
                                                error={!!meta.touched && !!meta.error}
                                            />
                                            {meta.touched && meta.error ? (
                                                <FormHelperText error={true} sx={{ ml: 1.75 }}>
                                                    <FormattedMessage id={meta.error} />
                                                </FormHelperText>
                                            ) : null}
                                        </>
                                    )}
                                </Field>
                                <PoolFormikSelect
                                    name={fieldNames.visibility}
                                    label={<FormattedMessage id="app.label.internalNotes.visibility" />}
                                    fullWidth={true}
                                    options={internalNoteVisibilityOptions}
                                    multiple={true}
                                    renderValue={renderVisibilityValue}
                                    required={true}
                                />
                            </Stack>
                        </PoolModalContent>
                        <PoolModalActions>
                            {/* This used to read "Löschen" in red with a bin, next to an
                                identically marked entry in the row menu that really
                                deletes. It never deleted anything — it discards the
                                edit and closes, which is what it now says. */}
                            <PoolButton variant="text" color="inherit" onClick={() => handleClose(formikProps)}>
                                <FormattedMessage id="app.common.cancel" />
                            </PoolButton>
                            <PoolButton onClick={() => formikProps.submitForm()}>
                                <FormattedMessage id={formikProps.values.id ? 'app.common.save' : 'app.common.add'} />
                            </PoolButton>
                        </PoolModalActions>
                    </PoolModal>
                )}
            </Formik>
        </PaloNextThemeProvider>
    );
};
